import { isTravelTypeWithHousing } from '@repo/utils'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { PdfTypeEnum } from '../../../../../../../lib/graphql'
import { Button } from '../../../../../../../shadcn/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../../../shadcn/components/ui/dialog'
import { Separator } from '../../../../../../../shadcn/components/ui/separator'
import { cn } from '../../../../../../../shadcn/utils'
import { Icon } from '../../../../../../atoms'
import { useTravelTypeContext } from '../../../../../../contexts'
import PdfCheckbox from '../../../../../../templates/client-request-cart/cart/modal-client-request-confirmation/PdfCheckbox'

interface IFormValues {
  isConfirmed: boolean
}

const UserCanConfirmBookingModal = ({
  isOpen,
  closeModal,
  onSubmit,
  clientRequestId,
  selectedProposalId,
  isLoadingSubmit,
}: {
  isOpen: boolean
  closeModal: () => void
  onSubmit: () => void
  clientRequestId: string
  selectedProposalId: string
  isLoadingSubmit: boolean
}) => {
  const { travelType } = useTravelTypeContext()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const intl = useIntl()

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      isConfirmed: false,
    },
  })

  const handleClick = handleSubmit(onSubmit)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent
        backdropStyle='dark'
        className='px-0 lg:max-w-xl'
        data-testid='modal'
      >
        <div className='flex flex-col gap-4'>
          <DialogHeader className='px-6'>
            <DialogTitle className='mb-6 flex'>
              <FormattedMessage
                id='ui.UserCanConfirmBookingModal.confirmBooking'
                defaultMessage='Confirmer la réservation ?'
              />
              <Icon
                className='ml-auto h-3 w-3'
                name='cross-without-circle'
                color='black'
                style={{ cursor: 'pointer' }}
                onClick={closeModal}
              />
            </DialogTitle>
          </DialogHeader>
          <div className={s.content}>
            <div className='px-6'>
              <div className={cn(s.title, 'mb-2')}>
                <FormattedMessage
                  id='ui.ModalClientRequestConfirmationUser.24hToConfirm'
                  defaultMessage='{hostType} dispose de 24h pour confirmer la réservation en fonction de ses disponibilités.'
                  values={{
                    hostType: isTravelTypeWithHousing(travelType) ? (
                      <FormattedMessage
                        id='ui.ModalClientRequestConfirmationUser.24hToConfirm.host'
                        defaultMessage='L’hôte'
                      />
                    ) : (
                      <FormattedMessage
                        id='ui.ModalClientRequestConfirmationUser.24hToConfirm.serviceProvider'
                        defaultMessage='Le prestataire'
                      />
                    ),
                  }}
                />
              </div>
              <div className={s.subtitle}>
                <FormattedMessage
                  id='ui.ModalClientRequestConfirmationUser.notifiedAtConfirmation'
                  defaultMessage='Vous serez notifié(e) par mail de l’acceptation et pourrez ainsi procéder au règlement de l’acompte.'
                />
              </div>
            </div>
            <Separator />
            <div className={cn(s.checkboxes, 'px-6')}>
              <PdfCheckbox
                name='isConfirmed'
                control={control}
                clientProposalId={selectedProposalId}
                clientRequestId={clientRequestId}
                setIsLoading={setIsLoading}
                pdfCheckboxText={[
                  {
                    label: intl.formatMessage({
                      id: 'ui.UserCanConfirmBookingModal.label',
                      defaultMessage:
                        'En cochant cette case, vous confirmez avoir lu et accepté les',
                    }),
                  },
                  {
                    label: ' ',
                  },
                  {
                    label: intl.formatMessage({
                      id: 'ui.UserCanConfirmBookingModal.cgu',
                      defaultMessage: 'Conditions générales d’utilisation',
                    }),
                    url: `${process.env.NEXT_PUBLIC_APP_URL}/conditions-generales`,
                  },
                  {
                    label: ', ',
                  },
                  {
                    label: intl.formatMessage({
                      id: 'ui.UserCanConfirmBookingModal.businessTripConditions',
                      defaultMessage: "Convention Voyage d'Affaires",
                    }),
                    pdfType: PdfTypeEnum.USER_BUSINESS_TRIP_CONDITIONS_UNSIGNED,
                  },
                  {
                    label: ', ',
                  },
                  {
                    label: intl.formatMessage({
                      id: 'ui.UserCanConfirmBookingModal.specialConditions',
                      defaultMessage: "Conditions générales de l'hôte",
                    }),
                    pdfType: PdfTypeEnum.OWNER_SPECIAL_CONDITIONS,
                  },
                  {
                    label: ' ',
                  },
                  {
                    label: intl.formatMessage({
                      id: 'ui.UserCanConfirmBookingModal.and',
                      defaultMessage: 'ainsi que le',
                    }),
                  },
                  {
                    label: ' ',
                  },
                  {
                    label: intl.formatMessage({
                      id: 'ui.UserCanConfirmBookingModal.userQuotation',
                      defaultMessage: 'Devis Afférent.',
                    }),
                    pdfType: PdfTypeEnum.USER_QUOTATION,
                  },
                ]}
              />
            </div>
          </div>
          <Separator />
          <DialogFooter className='px-6'>
            <Button
              onClick={handleClick}
              disabled={!isValid}
              isLoading={isLoadingSubmit || isLoading}
            >
              <FormattedMessage
                id='ui.UserCanConfirmBookingModal.confirmBookingFooter'
                defaultMessage='Confirmer la réservation'
              />
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UserCanConfirmBookingModal
